import { useState } from "react";
import {
  useGetApiUsageByOrganizationQuery,
  useGetApiUsageByUserQuery,
  useGetOrganizationsQuery,
  useGetUsersQuery,
} from "../../redux/api";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

// Must match MAX_USAGE_RANGE_DAYS in services/src/modules/api-usage/constant.ts,
// which rejects anything wider. The daily rows are only retained a little longer
// than this, so a wider window couldn't be answered accurately anyway — the
// pickers below are clamped so the limit is visible up front rather than
// arriving as an error after you pick a date.
const MAX_RANGE_DAYS = 31;

// `iso` shifted by `days`, staying on the YYYY-MM-DD calendar-day grid.
const shiftISO = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const earliestStart = (end: string) => shiftISO(end, -(MAX_RANGE_DAYS - 1));
const latestEnd = (start: string) => {
  const capped = shiftISO(start, MAX_RANGE_DAYS - 1);
  const today = todayISO();
  return capped < today ? capped : today;
};

const num = (n: number) => (n ?? 0).toLocaleString();
const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString() : "—";

// Platform Owner → Settings → API Usage. View-only report: call totals per
// organization, drilling into per-user / per-operation counts. Powered by the
// aggregated api-usage counters the Apollo plugin records on every request.
const ApiUsage = () => {
  const [startDay, setStartDay] = useState(daysAgoISO(29));
  const [endDay, setEndDay] = useState(todayISO());
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);

  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: users = [] } = useGetUsersQuery();

  const {
    data: orgRows = [],
    isFetching: orgLoading,
    refetch,
  } = useGetApiUsageByOrganizationQuery({ startDay, endDay });

  const { data: userRows = [], isFetching: userLoading } =
    useGetApiUsageByUserQuery(
      { organizationId: selectedOrg ?? "", startDay, endDay },
      { skip: !selectedOrg },
    );

  const orgName = (id: string) => {
    if (id === "anonymous") return "Anonymous / public";
    return organizations.find((o) => o.id === id)?.name ?? id;
  };

  const userName = (id: string) => {
    if (id === "anonymous") return "Anonymous";
    const u = users.find((x) => x.id === id);
    if (!u) return id;
    const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
    return name || u.email || id;
  };

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>API Usage</h2>
          </div>
          <div className="toolbar">
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={orgLoading}
            >
              {orgLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        <div className="form-grid">
          <label className="field">
            From
            <input
              type="date"
              value={startDay}
              min={earliestStart(endDay)}
              max={endDay}
              onChange={(e) => setStartDay(e.target.value)}
            />
          </label>
          <label className="field">
            To
            <input
              type="date"
              value={endDay}
              min={startDay}
              max={latestEnd(startDay)}
              onChange={(e) => setEndDay(e.target.value)}
            />
          </label>
        </div>
        <span className="field__hint">
          Day-level detail covers up to {MAX_RANGE_DAYS} days at a time.
        </span>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Calls</th>
                <th>Errors</th>
                <th>Users</th>
                <th>Last call</th>
              </tr>
            </thead>
            <tbody>
              {orgLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={5}>
                    Loading usage…
                  </td>
                </tr>
              ) : orgRows.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={5}>
                    No API calls recorded in this range.
                  </td>
                </tr>
              ) : (
                orgRows.map((row) => (
                  <tr
                    key={row.organizationId}
                    onClick={() =>
                      setSelectedOrg(
                        selectedOrg === row.organizationId
                          ? null
                          : row.organizationId,
                      )
                    }
                    style={{
                      cursor: "pointer",
                      background:
                        selectedOrg === row.organizationId
                          ? "var(--info-soft)"
                          : undefined,
                    }}
                  >
                    <td className="cell-title">{orgName(row.organizationId)}</td>
                    <td>{num(row.totalCalls)}</td>
                    <td>{num(row.totalErrors)}</td>
                    <td>{num(row.userCount)}</td>
                    <td className="cell-date">{fmtDate(row.lastAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {selectedOrg ? (
        <section className="panel">
          <div className="panel__head">
            <div>
              <h2 style={{ margin: 0, fontSize: "1.05rem" }}>
                {orgName(selectedOrg)} — users
              </h2>
              <p style={{ margin: "0.2rem 0 0", color: "var(--text-muted)" }}>
                Per-user and per-operation calls.
              </p>
            </div>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setSelectedOrg(null)}
            >
              Close
            </button>
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Operation</th>
                  <th>Calls</th>
                  <th>Errors</th>
                  <th>Last call</th>
                </tr>
              </thead>
              <tbody>
                {userLoading ? (
                  <tr>
                    <td className="data-table__empty" colSpan={5}>
                      Loading…
                    </td>
                  </tr>
                ) : userRows.length === 0 ? (
                  <tr>
                    <td className="data-table__empty" colSpan={5}>
                      No calls for this organization in this range.
                    </td>
                  </tr>
                ) : (
                  userRows.map((row) => (
                    <tr key={`${row.userId}:${row.operation}`}>
                      <td className="cell-title">{userName(row.userId)}</td>
                      <td>{row.operation}</td>
                      <td>{num(row.totalCalls)}</td>
                      <td>{num(row.totalErrors)}</td>
                      <td className="cell-date">{fmtDate(row.lastAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default ApiUsage;
